import React, { useState } from "react";
import { db } from "./firebase";
import { collection, getDocs, doc, updateDoc } from "./firebase-shim";

// LAV_BY_ID_CONV_v1 - conversione una tantum: aggancia ore e foto alle lavorazioni per id

const norm = s => (s || "").toString().toLowerCase().trim();
const haId = v => v !== undefined && v !== null && v !== "";
const fmt = n => (Math.round((n || 0) * 10) / 10).toLocaleString("it-IT");

const trovaIdx = (a, lavs) => {
  const L = lavs || [];
  if (!a) return -1;
  if (haId(a.lavorazioneId)) {
    const k = String(a.lavorazioneId);
    return L.findIndex(l => l && haId(l.id) && String(l.id) === k);
  }
  if (a.lavorazione) {
    const n = norm(a.lavorazione);
    const i = L.findIndex(l => l && norm(l.desc) === n);
    if (i >= 0) return i;
  }
  if (a.lavorazioneIdx != null) {
    const p = parseInt(a.lavorazioneIdx);
    if (!isNaN(p) && p >= 0 && p < L.length) return p;
  }
  return -1;
};

const pianifica = (cant, assenze, preventivi, seedRef) => {
  const cid = cant._id;
  const lavs = cant.lavorazioni || [];
  const nomeC = norm(cant.nome);
  const prevs = preventivi.filter(p => p._id === cant.preventivoId ||
    (p.stato === "accettato" && norm(p.nomeCantiere || (p.header && p.header.nomeCantiere)) === nomeC));
  const prevVoci = [];
  prevs.forEach(p => (p.lavorazioni || []).forEach(v => { if (v && haId(v.id)) prevVoci.push(v); }));
  const usati = new Set(lavs.filter(l => l && haId(l.id)).map(l => String(l.id)));
  let daPrev = 0, nuove = 0;
  const nuoveLavs = lavs.map(l => {
    if (!l || haId(l.id)) return l;
    const cand = new Map();
    prevVoci.forEach(v => { if (norm(v.desc) === norm(l.desc) && !usati.has(String(v.id))) cand.set(String(v.id), v.id); });
    let id;
    if (cand.size === 1) { id = [...cand.values()][0]; daPrev++; }
    else { id = seedRef.v++; nuove++; }
    usati.add(String(id));
    return { ...l, id };
  });

  const regs = assenze.filter(a => a && (a.tipo === "lavorato" || a.tipo === "foto") &&
    (a.cantiereId === cid || (cant.id && a.cantiereId === cant.id) || (!a.cantiereId && norm(a.cantiere) === nomeC)));
  const aggiorna = [];
  const sospetti = [];
  let gia = 0, senzaVoce = 0;
  const prima = {}, dopo = {};
  regs.forEach(a => {
    const ore = a.tipo === "lavorato" ? (parseFloat(a.ore) || 0) : 0;
    const idx = trovaIdx(a, lavs);
    if (idx >= 0) prima[idx] = (prima[idx] || 0) + ore;
    if (haId(a.lavorazioneId)) {
      gia++;
      const j = trovaIdx(a, nuoveLavs);
      if (j >= 0) dopo[j] = (dopo[j] || 0) + ore;
      return;
    }
    if (idx < 0) { senzaVoce++; return; }
    const nuovoId = nuoveLavs[idx].id;
    aggiorna.push({ _id: a._id, lavorazioneId: nuovoId });
    const j = trovaIdx({ ...a, lavorazioneId: nuovoId }, nuoveLavs);
    if (j >= 0) dopo[j] = (dopo[j] || 0) + ore;
    if (a.lavorazioneIdx != null) {
      const p = parseInt(a.lavorazioneIdx);
      if (!isNaN(p) && p >= 0 && p !== idx) {
        sospetti.push({ data: a.data, operaio: a.operaio, ore: a.ore, tipo: a.tipo,
          voce: (lavs[idx] || {}).desc, pos: p, vocePos: (lavs[p] || {}).desc });
      }
    }
  });
  const chiavi = new Set([...Object.keys(prima), ...Object.keys(dopo)]);
  const diff = [...chiavi].filter(k => Math.abs((prima[k] || 0) - (dopo[k] || 0)) > 0.001);
  const somma = o => Object.values(o).reduce((s, v) => s + v, 0);
  return { cant, cid, nuoveLavs, daPrev, nuove, vociCambiate: daPrev + nuove, aggiorna, gia, senzaVoce,
    sospetti, orePrima: somma(prima), oreDopo: somma(dopo), diff, nRegs: regs.length };
};

export default function AgganciaOreId() {
  const [aperto, setAperto] = useState(false);
  const [cantieri, setCantieri] = useState([]);
  const [scelta, setScelta] = useState("");
  const [piano, setPiano] = useState(null);
  const [pianoPer, setPianoPer] = useState("");
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState("");

  const caricaElenco = async () => {
    try {
      const s = await getDocs(collection(db, "cantieri"));
      const l = s.docs.map(d => ({ ...d.data(), _id: d.id })).sort((a, b) => norm(a.nome).localeCompare(norm(b.nome)));
      setCantieri(l);
    } catch (e) { alert("Errore: " + e.message); }
  };

  const anteprima = async () => {
    if (!scelta) { alert("Scegli un cantiere."); return; }
    setBusy(true); setProg("Leggo i dati..."); setPiano(null);
    try {
      const [cs, as, ps] = await Promise.all([
        getDocs(collection(db, "cantieri")), getDocs(collection(db, "assenze")), getDocs(collection(db, "preventivi"))]);
      const cants = cs.docs.map(d => ({ ...d.data(), _id: d.id }));
      const ass = as.docs.map(d => ({ ...d.data(), _id: d.id }));
      const prev = ps.docs.map(d => ({ ...d.data(), _id: d.id }));
      const seedRef = { v: Date.now() };
      const tutti = scelta === "__tutti__";
      const lista = tutti ? cants : cants.filter(c => c._id === scelta);
      const ris = lista.map(c => pianifica(c, ass, prev, seedRef))
        .filter(r => !tutti || r.vociCambiate > 0 || r.aggiorna.length > 0);
      setPiano(ris); setPianoPer(scelta);
    } catch (e) { alert("Errore: " + e.message); }
    finally { setBusy(false); setProg(""); }
  };

  const converti = async () => {
    if (!piano || pianoPer !== scelta) { alert("Fai prima l'anteprima."); return; }
    const conDiff = piano.filter(r => r.diff.length > 0);
    if (conDiff.length > 0) { alert("STOP: in " + conDiff.length + " cantieri le ore prima e dopo non coincidono. Non converto niente."); return; }
    const totV = piano.reduce((s, r) => s + r.vociCambiate, 0);
    const totR = piano.reduce((s, r) => s + r.aggiorna.length, 0);
    if (totV === 0 && totR === 0) { alert("Niente da convertire: e' gia' tutto agganciato."); return; }
    if (!window.confirm("Confermi la conversione?\n\nCantieri: " + piano.length + "\nVoci che ricevono l'id: " + totV +
      "\nGiornate e foto da agganciare: " + totR + "\n\nLe ore a schermo non cambiano.")) return;
    setBusy(true);
    let fatti = 0;
    try {
      for (const r of piano) {
        if (r.vociCambiate > 0) {
          setProg((r.cant.nome || "") + ": voci...");
          await updateDoc(doc(db, "cantieri", r.cid), { lavorazioni: r.nuoveLavs, updatedAt: new Date().toISOString() });
        }
        for (let k = 0; k < r.aggiorna.length; k += 20) {
          const blocco = r.aggiorna.slice(k, k + 20);
          await Promise.all(blocco.map(u => updateDoc(doc(db, "assenze", u._id), { lavorazioneId: u.lavorazioneId })));
          fatti += blocco.length;
          setProg((r.cant.nome || "") + ": " + fatti + " / " + totR);
        }
      }
      alert("Fatto. Registrazioni agganciate: " + fatti + ". Rifai l'anteprima per controllare: deve risultare tutto a zero.");
    } catch (e) {
      alert("Errore a meta': " + e.message + "\nRifai anteprima e converti: riprende da dove si e' fermato.");
    } finally { setBusy(false); setProg(""); setPiano(null); }
  };

  if (!aperto) {
    return (
      <div className="card" style={{ marginBottom: 16, padding: 12 }}>
        <button className="btn btn-sm" onClick={() => { setAperto(true); caricaElenco(); }}>🔗 Aggancio ore per id (una tantum)</button>
      </div>
    );
  }

  const th = { padding: "6px 8px", textAlign: "right", fontSize: 11, opacity: 0.7, whiteSpace: "nowrap" };
  const td = { padding: "6px 8px", textAlign: "right", fontSize: 13, borderTop: "1px solid rgba(128,128,128,0.25)" };
  const tot = piano ? piano.reduce((s, r) => ({
    v: s.v + r.vociCambiate, p: s.p + r.daPrev, n: s.n + r.nuove, a: s.a + r.aggiorna.length, g: s.g + r.gia,
    x: s.x + r.senzaVoce, q: s.q + r.sospetti.length, o1: s.o1 + r.orePrima, o2: s.o2 + r.oreDopo }),
    { v: 0, p: 0, n: 0, a: 0, g: 0, x: 0, q: 0, o1: 0, o2: 0 }) : null;

  return (
    <div className="card" style={{ marginBottom: 16, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>🔗 Aggancio ore per id (una tantum)</div>
        <button className="btn btn-sm" onClick={() => { setAperto(false); setPiano(null); }}>Chiudi</button>
      </div>
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 12 }}>
        Mette l'etichetta (id) a voci, giornate e foto gia' esistenti. Le ore a schermo non cambiano. Meglio farlo quando nessuno sta registrando ore.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <select value={scelta} onChange={e => { setScelta(e.target.value); setPiano(null); }} disabled={busy}
          style={{ padding: "6px 8px", borderRadius: 6, minWidth: 220 }}>
          <option value="">— scegli il cantiere —</option>
          <option value="__tutti__">★ Tutti i cantieri</option>
          {cantieri.map(c => <option key={c._id} value={c._id}>{c.nome || c._id}{c.completato ? " (chiuso)" : ""}</option>)}
        </select>
        <button className="btn btn-sm" onClick={anteprima} disabled={busy || !scelta}>👁 Anteprima</button>
        <button className="btn btn-primary btn-sm" onClick={converti} disabled={busy || !piano || pianoPer !== scelta}>✅ Converti</button>
        {prog && <span style={{ fontSize: 12, opacity: 0.8 }}>{prog}</span>}
      </div>

      {piano && piano.length === 0 && <div style={{ fontSize: 13 }}>Niente da convertire: e' gia' tutto agganciato.</div>}

      {piano && piano.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Cantiere</th>
                <th style={th}>Voci senza id (da prev. / nuove)</th>
                <th style={th}>Da agganciare</th>
                <th style={th}>Gia' agganciate</th>
                <th style={th}>Senza voce</th>
                <th style={th}>Sospetti</th>
                <th style={th}>Ore prima → dopo</th>
              </tr>
            </thead>
            <tbody>
              {piano.map(r => (
                <tr key={r.cid}>
                  <td style={{ ...td, textAlign: "left" }}>{r.cant.nome}</td>
                  <td style={td}>{r.vociCambiate} ({r.daPrev} / {r.nuove})</td>
                  <td style={td}>{r.aggiorna.length}</td>
                  <td style={td}>{r.gia}</td>
                  <td style={td}>{r.senzaVoce}</td>
                  <td style={{ ...td, color: r.sospetti.length ? "#d97706" : undefined }}>{r.sospetti.length}</td>
                  <td style={{ ...td, color: r.diff.length ? "#dc2626" : "#16a34a", fontWeight: 700 }}>
                    {fmt(r.orePrima)} → {fmt(r.oreDopo)} {r.diff.length ? "⚠" : "✓"}
                  </td>
                </tr>
              ))}
              {piano.length > 1 && (
                <tr style={{ fontWeight: 700 }}>
                  <td style={{ ...td, textAlign: "left" }}>TOTALE ({piano.length})</td>
                  <td style={td}>{tot.v} ({tot.p} / {tot.n})</td>
                  <td style={td}>{tot.a}</td>
                  <td style={td}>{tot.g}</td>
                  <td style={td}>{tot.x}</td>
                  <td style={td}>{tot.q}</td>
                  <td style={td}>{fmt(tot.o1)} → {fmt(tot.o2)}</td>
                </tr>
              )}
            </tbody>
          </table>
          {piano.length === 1 && piano[0].sospetti.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Casi sospetti (nome e posizione puntano a voci diverse): vengono agganciati alla voce col nome, come lo schermo mostra oggi.</div>
              {piano[0].sospetti.slice(0, 30).map((s, k) => (
                <div key={k} style={{ padding: "3px 0", borderTop: "1px solid rgba(128,128,128,0.2)" }}>
                  {s.data} · {s.operaio} · {s.tipo === "foto" ? "foto" : s.ore + "h"} → <b>{s.voce}</b> (la posizione diceva #{s.pos + 1}: {s.vocePos || "—"})
                </div>
              ))}
              {piano[0].sospetti.length > 30 && <div style={{ opacity: 0.7 }}>... e altri {piano[0].sospetti.length - 30}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
